import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import coApplicantService from "services/coApplicant";

// --- Initial State ---
const initialState = {
  coApplicants: {
    items: [],
    totalCount: 0,
  },
  getCoApplicants: {
    status: "idle",
    error: false,
  },
  addCoApplicant: {
    status: "idle",
    error: false,
  },
};

// --- Async Thunks ---
export const getCoApplicantAsync = createAsyncThunk(
  "coApplicants/getCoApplicantAsync",
  async (queryParams, { rejectWithValue }) => {
    try {
      const res = await coApplicantService.getAll(queryParams);
      return res.data;
    } catch (error) {
      return rejectWithValue(
        error?.response?.data || error.message || "Unknown error"
      );
    }
  }
);

export const addCoApplicantAsync = createAsyncThunk(
  "coApplicants/addCoApplicantAsync",
  async ({ data }, { rejectWithValue }) => {
    try {
      const res = await coApplicantService.create(data);
      return res.data;
    } catch (error) {
      return rejectWithValue(
        error?.response?.data || error.message || "Unknown error"
      );
    }
  }
);

export const editCoApplicantAsync = createAsyncThunk(
  "coApplicants/editCoApplicantAsync",
  async ({ coApplicantId, data }, { rejectWithValue }) => {
    try {
      const res = await coApplicantService.update(coApplicantId, data);
      return res.data;
    } catch (error) {
      return rejectWithValue(
        error?.response?.data || error.message || "Unknown error"
      );
    }
  }
);

export const deleteCoApplicantAsync = createAsyncThunk(
  "coApplicants/deleteCoApplicantAsync",
  async ({ coApplicantId }, { rejectWithValue }) => {
    try {
      const res = await coApplicantService.remove(coApplicantId);
      return res.data;
    } catch (error) {
      return rejectWithValue(
        error?.response?.data || error.message || "Unknown error"
      );
    }
  }
);

export const acceptCoApplicantInviteAsync = createAsyncThunk(
  "coApplicants/acceptCoApplicantInviteAsync",
  async ({ token }, { rejectWithValue }) => {
    try {
      const res = await coApplicantService.acceptCoApplicantInvite(token);
      return res.data;
    } catch (error) {
      return rejectWithValue(
        error?.response?.data || error.message || "Unknown error"
      );
    }
  }
);

export const sendAccessRequestForCoApplicantAsync = createAsyncThunk(
  "coApplicants/sendAccessRequestForCoApplicantAsync",
  async ({ data }, { rejectWithValue }) => {
    try {
      const res = await coApplicantService.sendAccessRequestForCoApplicant(
        data
      );
      return res.data;
    } catch (error) {
      return rejectWithValue(
        error?.response?.data || error.message || "Unknown error"
      );
    }
  }
);

export const reinstateCoApplicantAsync = createAsyncThunk(
  "coApplicants/reinstateCoApplicantAsync",
  async ({ coApplicantId }, { rejectWithValue }) => {
    try {
      const res = await coApplicantService.reinstateCoApplicant(coApplicantId);
      return res.data;
    } catch (error) {
      return rejectWithValue(
        error?.response?.data || error.message || "Unknown error"
      );
    }
  }
);

export const resendCoApplicantInviteAsync = createAsyncThunk(
  "coApplicants/resendCoApplicantInviteAsync",
  async ({ coApplicantId }, { rejectWithValue }) => {
    try {
      const res = await coApplicantService.resendCoApplicantInvite(
        coApplicantId
      );
      return res.data;
    } catch (error) {
      return rejectWithValue(
        error?.response?.data || error.message || "Unknown error"
      );
    }
  }
);

// --- Slice ---
const coApplicantsSlice = createSlice({
  name: "coApplicants",
  initialState,
  reducers: {
    resetCoApplicantsSlice: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      .addCase(getCoApplicantAsync.pending, (state) => {
        state.getCoApplicants.status = "loading";
        state.getCoApplicants.error = false;
      })
      .addCase(getCoApplicantAsync.fulfilled, (state, action) => {
        state.getCoApplicants.status = "idle";
        state.coApplicants = action.payload;
      })
      .addCase(getCoApplicantAsync.rejected, (state, action) => {
        state.getCoApplicants.status = "failed";
        state.getCoApplicants.error =
          action?.payload || "Failed to fetch co-applicants";
      })
      .addCase(addCoApplicantAsync.pending, (state) => {
        state.addCoApplicant.status = "loading";
        state.addCoApplicant.error = false;
      })
      .addCase(addCoApplicantAsync.fulfilled, (state, action) => {
        if (action.payload.coApplicant) {
          state.coApplicants.items.push(action.payload.coApplicant);
          state.coApplicants.totalCount += 1;
        }
        state.addCoApplicant.status = "idle";
      })
      .addCase(addCoApplicantAsync.rejected, (state, action) => {
        state.addCoApplicant.status = "failed";
        state.addCoApplicant.error =
          action?.payload || "Failed to add co-applicant";
      })
      .addCase(editCoApplicantAsync.fulfilled, (state, action) => {
        const { payload } = action;
        const index = state.coApplicants.items.findIndex(
          (coApplicant) => payload._id === coApplicant._id
        );
        if (index > -1) {
          state.coApplicants.items.splice(index, 1, payload);
        }
      })
      .addCase(deleteCoApplicantAsync.fulfilled, (state, action) => {
        const { payload } = action;
        const index = state.coApplicants.items.findIndex(
          (coApplicant) => payload._id === coApplicant._id
        );
        if (index > -1) {
          if (["Pending", "Opened", "Denied"].includes(payload.status)) {
            state.coApplicants.items.splice(index, 1);
          } else {
            state.coApplicants.items.splice(index, 1, payload);
          }
        }
      })
      .addCase(reinstateCoApplicantAsync.fulfilled, (state, action) => {
        const { payload } = action;
        const index = state.coApplicants.items.findIndex(
          (coApplicant) => payload._id === coApplicant._id
        );
        if (index > -1) {
          state.coApplicants.items.splice(index, 1, payload);
        }
      })
      .addCase(
        sendAccessRequestForCoApplicantAsync.fulfilled,
        (state, action) => {
          if (action.payload.coApplicant) {
            state.coApplicants.items.push(action.payload.coApplicant);
            state.coApplicants.totalCount += 1;
          }
        }
      );
  },
});

// --- Exports ---
export const { resetCoApplicantsSlice } = coApplicantsSlice.actions;
export default coApplicantsSlice.reducer;
